import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Eén overlay voor twee vormen: op mobiel een sheet die vanaf de onderrand
 * opkomt, vanaf sm een gecentreerde dialoog. Dat is precies het verschil tussen
 * ronde 8A en 9A, en tussen 10B en 11B — dezelfde inhoud, andere plaatsing.
 *
 * Geen `<dialog>`: die zet zichzelf in de top layer en dan is de sheet niet
 * meer met de rest van de layout af te stemmen. Focus en Escape doen we hier
 * met de hand, dat is precies zo veel als dit nodig heeft.
 *
 * <b>Het schuiven.</b> Twee kanten, twee mechanismen.
 *
 * Binnenkomen doet de browser zelf met `@starting-style` (de `starting:`-klassen
 * hieronder): het paneel komt meteen op zijn eindstand in de boom en de browser
 * animeert vanaf de startstand daarnaartoe. Geen frame afwachten, geen extra
 * stand om bij te houden.
 *
 * Weggaan kan dat niet, want zodra `open` uit gaat zou React het paneel meteen
 * weghalen en valt er niets meer te animeren. Daar houdt `leaving` hem nog even
 * in de boom. Die vlag gaat aan zodra er iets ópen is — dus ruim vóór het
 * moment dat hij nodig is. Dat is geen omweg maar de kern: op het moment dat
 * `open` omslaat mag er niets meer bij te stellen zijn.
 *
 * Wat hier eerst stond — de stand bijstellen tijdens de render, het gangbare
 * recept — werkte niet. `open` hangt aan `?panel=` in de querystring, en
 * react-router zet die verandering in een transition weg. Een render-phase
 * update die in zo'n render wordt ingeschoten overleeft de herhaling niet: het
 * paneel kwam in de boom en was een render later weer weg, zonder dat er iets
 * gesloten werd.
 */

/** Even lang als de `duration` op de klassen hieronder. */
const Duration = 260

export function Sheet({
  open,
  onClose,
  labelledBy,
  width = 'md',
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy: string
  width?: 'md' | 'lg'
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)

  const [leaving, setLeaving] = useState(false)

  // Afgeleid en niet bijgehouden: zo staat het paneel in dezelfde render in de
  // boom waarin `open` aangaat, en hoeft er op het moment van omslaan niets
  // meer bijgesteld te worden.
  const mounted = open || leaving

  useEffect(() => {
    // Beide kanten via een timer, want een setState rechtstreeks in een effect
    // is een extra tekenbeurt bovenop de beurt die er net was. Nul milliseconde
    // volstaat: deze vlag is pas nodig bij het sluiten.
    const timer = setTimeout(
      () => setLeaving(open),

      // Korter en het paneel knipt halverwege af, langer en er hangt een
      // onzichtbaar paneel over het scherm dat je klikken opvangt.
      open ? 0 : Duration,
    )

    return () => clearTimeout(timer)
  }, [open])

  // Op `open` en niet op `mounted`: tijdens het wegschuiven hoort Escape al
  // niets meer te doen en mag de pagina weer scrollen.
  useEffect(() => {
    if (!open) {
      return
    }

    const previous = document.activeElement as HTMLElement | null

    // Naar het paneel, niet naar het eerste veld: anders springt op een
    // telefoon meteen het toetsenbord open terwijl de gast nog leest.
    panel.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      previous?.focus()
    }
  }, [open, onClose])

  if (!mounted) {
    return null
  }

  return (
    <div
      // Een paneel dat al aan het wegschuiven is hoort geen klikken meer op te
      // vangen: daar staat voor de gast niets meer.
      className={
        'fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-6 ' +
        (open ? '' : 'pointer-events-none')
      }
    >
      <button
        type="button"
        aria-label="Sluiten"
        onClick={onClose}
        className={
          'absolute inset-0 cursor-default transition-opacity duration-260 ease-out ' +
          'motion-reduce:transition-none starting:opacity-0 ' +
          (open ? 'opacity-100' : 'opacity-0')
        }
        style={{ background: 'var(--scrim)' }}
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={
          'bg-shell relative max-h-full w-full overflow-y-auto rounded-t-3xl px-6 pt-3.5 pb-8 ' +
          'shadow-dialog sm:rounded-dialog outline-none sm:px-9 sm:pt-8 sm:pb-7 ' +
          'transition duration-260 ease-out will-change-[translate,opacity] motion-reduce:transition-none ' +
          // Op mobiel schuift hij van onderen op. Vanaf sm is het een dialoog
          // midden op het scherm: die van onderaf laten komen gleed langs de
          // hele pagina, dus daar is het een korte lift met een vervaging.
          'starting:translate-y-full sm:starting:translate-y-2 ' +
          'sm:starting:scale-[0.98] sm:starting:opacity-0 ' +
          (open
            ? 'translate-y-0 sm:scale-100 sm:opacity-100'
            : 'translate-y-full sm:translate-y-2 sm:scale-[0.98] sm:opacity-0') +
          ' ' +
          (width === 'lg' ? 'sm:max-w-[620px]' : 'sm:max-w-[560px]')
        }
      >
        {/* Het greepje van de sheet; op desktop is er niets om te slepen. */}
        <div className="bg-line mx-auto mb-5 h-1 w-9.5 rounded-full sm:hidden" />

        {children}
      </div>
    </div>
  )
}
